<template>
  <!-- ============================================================ -->
  <!-- MODO EMBEDDED: Apenas o conteúdo, sem wrapper modal próprio -->
  <!-- ============================================================ -->
  <div v-if="embedded" class="audit-log-embedded">
    <div class="px-3 py-3">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">A carregar logs...</span>
        </div>
      </div>

      <div v-else-if="erro" class="alert alert-danger border-0 shadow-sm">
        <i class="bi bi-exclamation-triangle-fill me-2"></i> {{ erro }}
      </div>

      <template v-else>
        <!-- GUARDIÃO: Filtro de Cadastros Relacionados + Filtro por Tabela -->
        <div v-if="tabelasFilhas && tabelasFilhas.length > 0" class="d-flex align-items-center flex-wrap gap-2 mb-3" :class="mostrarRelacionados && tabelasDisponiveis.length > 1 ? 'justify-content-between' : 'justify-content-end'">
          <!-- Badges de filtro por tabela (visíveis apenas com cadastros relacionados) -->
          <div v-if="mostrarRelacionados && tabelasDisponiveis.length > 1" class="d-flex flex-wrap align-items-center gap-2">
            <span class="text-body-secondary small fw-medium me-1">Clique para filtrar:</span>
            <button
              v-for="tbl in tabelasDisponiveis"
              :key="tbl"
              type="button"
              class="badge rounded-pill border px-3 py-2 cursor-pointer text-decoration-none"
              :class="tabelaSelecionada === tbl
                ? 'bg-primary text-white border-primary shadow-sm'
                : 'bg-body text-body-secondary border-secondary-subtle'"
              @click="alternarFiltroTabela(tbl)"
            >
              <i v-if="tabelaSelecionada === tbl" class="bi bi-check2 me-1"></i>
              {{ formatarNomeTabela(tbl) }}
            </button>
            <button
              v-if="tabelaSelecionada"
              type="button"
              class="badge rounded-pill bg-danger-subtle text-danger-emphasis border border-danger-subtle px-3 py-2 cursor-pointer"
              @click="limparFiltroTabela"
            >
              <i class="bi bi-x-lg me-1"></i> Limpar
            </button>
          </div>

          <div class="d-inline-flex align-items-center bg-body border border-secondary-subtle rounded-pill px-3 py-2 shadow-sm">
            <div class="form-check form-switch mb-0 d-flex align-items-center p-0 m-0">
              <input class="form-check-input switch-md m-0 me-2 cursor-pointer" type="checkbox" id="chkRelacionados" v-model="mostrarRelacionados" style="margin-left: 0;">
              <label class="form-check-label text-body-secondary small fw-medium cursor-pointer mb-0" for="chkRelacionados">
                Ver logs de cadastros relacionados
              </label>
            </div>
          </div>
        </div>

        <!-- Campo de busca textual -->
        <div class="d-flex align-items-center gap-2 mb-3">
          <div class="input-group input-group-sm" style="max-width: 320px;">
            <span class="input-group-text bg-body-secondary border-secondary-subtle">
              <i class="bi bi-search"></i>
            </span>
            <input
              type="text"
              class="form-control form-control-sm"
              id="termoBusca"
              v-model="termoBusca"
              placeholder="Pesquisar (Enter para termos curtos)..."
              @keyup.enter="confirmarBusca"
            />
            <button
              v-if="termoBusca"
              type="button"
              class="btn btn-sm btn-outline-secondary"
              @click="limparBusca"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        <div v-if="logsFiltrados.length === 0" class="text-center text-body-secondary py-5 border rounded bg-body">
          <i class="bi bi-inbox fs-1 d-block mb-3 text-secondary"></i>
          Nenhum registo de auditoria encontrado para este filtro.
        </div>

        <!-- Tabela de Logs Formatada -->
        <div v-else class="table-responsive rounded shadow-sm bg-body border border-secondary-subtle">
          <table class="table table-hover table-bordered align-middle mb-0 text-sm">
            <thead class="table-secondary text-body-secondary">
              <tr>
                <th style="width: 15%;">Data / Hora</th>
                <th style="width: 15%;">Responsável</th>
                <th style="width: 10%;" class="text-center">Ação</th>
                <th style="width: 20%;">Campo Afetado</th>
                <th style="width: 20%;">Valor Anterior</th>
                <th style="width: 20%;">Novo Valor</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="log in logsPaginados" :key="log.id">

                <!-- Cenário 1: Edição -->
                <template v-if="log.tipoAcao === 'Modified'">
                  <tr v-for="(coluna, index) in obterColunasFiltradas(log)" :key="coluna">
                    <td v-if="index === 0" :rowspan="obterColunasFiltradas(log).length" class="text-nowrap fw-medium text-body-secondary">
                      {{ formatarDataHoraBR(log.dataHora) }}
                    </td>
                    <td v-if="index === 0" :rowspan="obterColunasFiltradas(log).length" class="text-break">
                      <i class="bi bi-person-fill me-1 text-body-secondary"></i>
                      <span v-if="log.usuarioId === 'Sistema'">Sistema</span>
                      <span v-else-if="isIdNumerico(log.usuarioId)">
                        ({{ log.usuarioId }}) {{ usuariosNomes[log.usuarioId] || '...' }}
                      </span>
                      <span v-else>{{ log.usuarioId }}</span>
                    </td>
                    <td v-if="index === 0" :rowspan="obterColunasFiltradas(log).length" class="text-center">
                      <span class="badge" :class="obterCorAcao(log.tipoAcao)">{{ traduzirAcao(log.tipoAcao) }}</span>
                      <div v-if="log.tabela !== tabela" class="mt-1 fw-bold text-body-secondary" style="font-size: 0.70rem; letter-spacing: 0.5px;">
                        {{ formatarNomeTabela(log.tabela) }}
                      </div>
                    </td>
                    <td class="fw-bold text-body-emphasis">{{ formatarNomeCampo(coluna) }}</td>
                    <td class="text-danger-emphasis bg-danger-subtle text-break">
                      <template v-if="isValorJson(log.dadosAntigos, coluna)">
                        <ul class="list-unstyled mb-0 small">
                          <li v-for="(v, k) in obterValorJson(log.dadosAntigos, coluna)" :key="k">
                            <strong class="text-body-emphasis">{{ formatarNomeCampo(k) }}:</strong>
                            <template v-if="typeof v === 'object' && v !== null">{{ JSON.stringify(v) }}</template>
                            <template v-else>{{ v }}</template>
                          </li>
                        </ul>
                      </template>
                      <template v-else>{{ obterValorApresentacao(log.dadosAntigos, coluna) }}</template>
                    </td>
                    <td class="text-success-emphasis bg-success-subtle text-break">
                      <template v-if="isValorJson(log.dadosNovos, coluna)">
                        <ul class="list-unstyled mb-0 small">
                          <li v-for="(v, k) in obterValorJson(log.dadosNovos, coluna)" :key="k">
                            <strong class="text-body-emphasis">{{ formatarNomeCampo(k) }}:</strong>
                            <template v-if="typeof v === 'object' && v !== null">{{ JSON.stringify(v) }}</template>
                            <template v-else>{{ v }}</template>
                          </li>
                        </ul>
                      </template>
                      <template v-else>{{ obterValorApresentacao(log.dadosNovos, coluna) }}</template>
                    </td>
                  </tr>
                </template>

                <!-- Cenário 2: Criação ou Exclusão -->
                <tr v-else>
                  <td class="text-nowrap fw-medium text-body-secondary">{{ formatarDataHoraBR(log.dataHora) }}</td>
                  <td class="text-break">
                    <i class="bi bi-person-fill me-1 text-body-secondary"></i>
                    <span v-if="log.usuarioId === 'Sistema'">Sistema</span>
                    <span v-else-if="isIdNumerico(log.usuarioId)">
                      ({{ log.usuarioId }}) {{ usuariosNomes[log.usuarioId] || '...' }}
                    </span>
                    <span v-else>{{ log.usuarioId }}</span>
                  </td>
                  <td class="text-center">
                    <span class="badge" :class="obterCorAcao(log.tipoAcao)">{{ traduzirAcao(log.tipoAcao) }}</span>
                    <div v-if="log.tabela !== tabela" class="mt-1 fw-bold text-body-secondary" style="font-size: 0.70rem; letter-spacing: 0.5px;">
                      {{ formatarNomeTabela(log.tabela) }}
                    </div>
                  </td>
                  <td class="fw-bold text-body-secondary fst-italic">Todos os Dados</td>
                  <td class="text-danger-emphasis bg-danger-subtle">
                    <span v-if="log.tipoAcao === 'Added'" class="text-body-secondary fw-bold">-</span>
                    <ul v-else class="list-unstyled mb-0 small">
                      <li v-for="(val, key) in filtrarCamposSistema(log.dadosAntigos)" :key="key">
                        <strong class="text-body-emphasis">{{ formatarNomeCampo(key) }}:</strong>
                        <template v-if="isValorJson(log.dadosAntigos, key)">
                          <ul class="mb-0 small ps-3" style="list-style: none;">
                            <li v-for="(v, k) in obterValorJson(log.dadosAntigos, key)" :key="k">
                              <strong class="text-body-emphasis">{{ formatarNomeCampo(k) }}:</strong>
                              <template v-if="typeof v === 'object' && v !== null">{{ JSON.stringify(v) }}</template>
                              <template v-else>{{ v }}</template>
                            </li>
                          </ul>
                        </template>
                        <template v-else>{{ obterValorApresentacao(log.dadosAntigos, key) }}</template>
                      </li>
                    </ul>
                  </td>
                  <td class="text-success-emphasis bg-success-subtle">
                    <span v-if="log.tipoAcao === 'Deleted'" class="text-body-secondary fw-bold">-</span>
                    <ul v-else class="list-unstyled mb-0 small">
                      <li v-for="(val, key) in filtrarCamposSistema(log.dadosNovos)" :key="key">
                        <strong class="text-body-emphasis">{{ formatarNomeCampo(key) }}:</strong>
                        <template v-if="isValorJson(log.dadosNovos, key)">
                          <ul class="mb-0 small ps-3" style="list-style: none;">
                            <li v-for="(v, k) in obterValorJson(log.dadosNovos, key)" :key="k">
                              <strong class="text-body-emphasis">{{ formatarNomeCampo(k) }}:</strong>
                              <template v-if="typeof v === 'object' && v !== null">{{ JSON.stringify(v) }}</template>
                              <template v-else>{{ v }}</template>
                            </li>
                          </ul>
                        </template>
                        <template v-else>{{ obterValorApresentacao(log.dadosNovos, key) }}</template>
                      </li>
                    </ul>
                  </td>
                </tr>

              </template>
            </tbody>
          </table>
        </div>

        <!-- Controles de Paginação -->
        <div v-if="logsFiltrados.length > 0" class="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3">
          <div class="d-flex align-items-center mb-2 mb-md-0">
            <label class="text-body-secondary small me-2 mb-0">Registos por página:</label>
            <select class="form-select form-select-sm w-auto cursor-pointer" v-model="itensPorPagina" @change="paginaAtual = 1">
              <option v-for="op in [5, 10, 25, 50]" :key="op" :value="op">{{ op }}</option>
            </select>
          </div>
          <div class="d-flex align-items-center">
            <span class="text-body-secondary small me-3">Página {{ paginaAtual }} de {{ totalPaginas || 1 }} (Total: {{ logsFiltrados.length }})</span>
            <button class="btn btn-sm btn-outline-secondary me-1" @click="paginaAtual--" :disabled="paginaAtual <= 1"><i class="bi bi-chevron-left"></i></button>
            <button class="btn btn-sm btn-outline-secondary" @click="paginaAtual++" :disabled="paginaAtual >= totalPaginas"><i class="bi bi-chevron-right"></i></button>
          </div>
        </div>
      </template>
    </div>
  </div>

  <!-- ============================================================ -->
  <!-- MODO MODAL: Wrapper completo com backdrop -->
  <!-- ============================================================ -->
  <Teleport v-else to="body">
  <div ref="backdropRef" class="modal-backdrop" :class="{ 'modal-backdrop-draggable': draggable }" :style="{ zIndex: zIndexEfetivo }" @click.self="fechar">
    <div ref="modalRef" class="modal-container modal-xl shadow" role="dialog" aria-modal="true" :style="modalStyle">

      <header
        class="modal-header"
        :class="{ 'cursor-grab': draggable, 'cursor-grabbing': draggable && dragging }"
        @mousedown.self="iniciarArrasto"
      >
        <div class="modal-header-info">
          <h5 class="modal-title d-flex align-items-center">
            <i class="bi bi-clock-history me-2"></i> Histórico de Alterações
          </h5>
          <small class="text-light opacity-75 mt-1 d-block" v-if="tituloRegistro">
            Registo: <strong>{{ tituloRegistro }}</strong> (ID: {{ chavePrimaria }})
          </small>
        </div>
        <button type="button" class="btn-close btn-close-white" @click="fechar" aria-label="Fechar"></button>
      </header>

      <div class="modal-body bg-body-tertiary px-4 py-3">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">A carregar logs...</span>
          </div>
        </div>

        <div v-else-if="erro" class="alert alert-danger border-0 shadow-sm">
          <i class="bi bi-exclamation-triangle-fill me-2"></i> {{ erro }}
        </div>

        <template v-else>
          <!-- GUARDIÃO: Filtro de Cadastros Relacionados + Filtro por Tabela -->
          <div v-if="tabelasFilhas && tabelasFilhas.length > 0" class="d-flex align-items-center flex-wrap gap-2 mb-3" :class="mostrarRelacionados && tabelasDisponiveis.length > 1 ? 'justify-content-between' : 'justify-content-end'">
            <!-- Badges de filtro por tabela (visíveis apenas com cadastros relacionados) -->
            <div v-if="mostrarRelacionados && tabelasDisponiveis.length > 1" class="d-flex flex-wrap align-items-center gap-2">
              <span class="text-body-secondary small fw-medium me-1">Clique para filtrar:</span>
              <button
                v-for="tbl in tabelasDisponiveis"
                :key="tbl"
                type="button"
                class="badge rounded-pill border px-3 py-2 cursor-pointer text-decoration-none"
                :class="tabelaSelecionada === tbl
                  ? 'bg-primary text-white border-primary shadow-sm'
                  : 'bg-body text-body-secondary border-secondary-subtle'"
                @click="alternarFiltroTabela(tbl)"
              >
                <i v-if="tabelaSelecionada === tbl" class="bi bi-check2 me-1"></i>
                {{ formatarNomeTabela(tbl) }}
              </button>
              <button
                v-if="tabelaSelecionada"
                type="button"
                class="badge rounded-pill bg-danger-subtle text-danger-emphasis border border-danger-subtle px-3 py-2 cursor-pointer"
                @click="limparFiltroTabela"
              >
                <i class="bi bi-x-lg me-1"></i> Limpar
              </button>
            </div>

            <div class="d-inline-flex align-items-center bg-body border border-secondary-subtle rounded-pill px-3 py-2 shadow-sm">
              <div class="form-check form-switch mb-0 d-flex align-items-center p-0 m-0">
                <input class="form-check-input switch-md m-0 me-2 cursor-pointer" type="checkbox" id="chkRelacionados" v-model="mostrarRelacionados" style="margin-left: 0;">
                <label class="form-check-label text-body-secondary small fw-medium cursor-pointer mb-0" for="chkRelacionados">
                  Ver logs de cadastros relacionados
                </label>
              </div>
            </div>
          </div>

          <!-- Campo de busca textual -->
          <div class="d-flex align-items-center gap-2 mb-3">
            <div class="input-group input-group-sm" style="max-width: 320px;">
              <span class="input-group-text bg-body-secondary border-secondary-subtle">
                <i class="bi bi-search"></i>
              </span>
              <input
                type="text"
                class="form-control form-control-sm"
                id="termoBusca"
                v-model="termoBusca"
                placeholder="Pesquisar (Enter para termos curtos)..."
                @keyup.enter="confirmarBusca"
              />
              <button
                v-if="termoBusca"
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @click="limparBusca"
              >
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
          </div>

          <div v-if="logsFiltrados.length === 0" class="text-center text-body-secondary py-5 border rounded bg-body">
            <i class="bi bi-inbox fs-1 d-block mb-3 text-secondary"></i>
            Nenhum registo de auditoria encontrado para este filtro.
          </div>

          <!-- Tabela de Logs Formatada -->
          <div v-else class="table-responsive rounded shadow-sm bg-body border border-secondary-subtle">
            <table class="table table-hover table-bordered align-middle mb-0 text-sm">
              <thead class="table-secondary text-body-secondary">
                <tr>
                  <th style="width: 15%;">Data / Hora</th>
                  <th style="width: 15%;">Responsável</th>
                  <th style="width: 10%;" class="text-center">Ação</th>
                  <th style="width: 20%;">Campo Afetado</th>
                  <th style="width: 20%;">Valor Anterior</th>
                  <th style="width: 20%;">Novo Valor</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="log in logsPaginados" :key="log.id">

                  <!-- Cenário 1: Edição -->
                  <template v-if="log.tipoAcao === 'Modified'">
                    <tr v-for="(coluna, index) in obterColunasFiltradas(log)" :key="coluna">
                      <td v-if="index === 0" :rowspan="obterColunasFiltradas(log).length" class="text-nowrap fw-medium text-body-secondary">
                        {{ formatarDataHoraBR(log.dataHora) }}
                      </td>
                      <td v-if="index === 0" :rowspan="obterColunasFiltradas(log).length" class="text-break">
                        <i class="bi bi-person-fill me-1 text-body-secondary"></i>
                        <span v-if="log.usuarioId === 'Sistema'">Sistema</span>
                        <span v-else-if="isIdNumerico(log.usuarioId)">
                          ({{ log.usuarioId }}) {{ usuariosNomes[log.usuarioId] || '...' }}
                        </span>
                        <span v-else>{{ log.usuarioId }}</span>
                      </td>
                      <td v-if="index === 0" :rowspan="obterColunasFiltradas(log).length" class="text-center">
                        <span class="badge" :class="obterCorAcao(log.tipoAcao)">{{ traduzirAcao(log.tipoAcao) }}</span>
                        <div v-if="log.tabela !== tabela" class="mt-1 fw-bold text-body-secondary" style="font-size: 0.70rem; letter-spacing: 0.5px;">
                          {{ formatarNomeTabela(log.tabela) }}
                        </div>
                      </td>
                      <td class="fw-bold text-body-emphasis">{{ formatarNomeCampo(coluna) }}</td>
                      <td class="text-danger-emphasis bg-danger-subtle text-break">
                        <template v-if="isValorJson(log.dadosAntigos, coluna)">
                          <ul class="list-unstyled mb-0 small">
                            <li v-for="(v, k) in obterValorJson(log.dadosAntigos, coluna)" :key="k">
                              <strong class="text-body-emphasis">{{ formatarNomeCampo(k) }}:</strong>
                              <template v-if="typeof v === 'object' && v !== null">{{ JSON.stringify(v) }}</template>
                              <template v-else>{{ v }}</template>
                            </li>
                          </ul>
                        </template>
                        <template v-else>{{ obterValorApresentacao(log.dadosAntigos, coluna) }}</template>
                      </td>
                      <td class="text-success-emphasis bg-success-subtle text-break">
                        <template v-if="isValorJson(log.dadosNovos, coluna)">
                          <ul class="list-unstyled mb-0 small">
                            <li v-for="(v, k) in obterValorJson(log.dadosNovos, coluna)" :key="k">
                              <strong class="text-body-emphasis">{{ formatarNomeCampo(k) }}:</strong>
                              <template v-if="typeof v === 'object' && v !== null">{{ JSON.stringify(v) }}</template>
                              <template v-else>{{ v }}</template>
                            </li>
                          </ul>
                        </template>
                        <template v-else>{{ obterValorApresentacao(log.dadosNovos, coluna) }}</template>
                      </td>
                    </tr>
                  </template>

                  <!-- Cenário 2: Criação ou Exclusão -->
                  <tr v-else>
                    <td class="text-nowrap fw-medium text-body-secondary">{{ formatarDataHoraBR(log.dataHora) }}</td>
                    <td class="text-break">
                      <i class="bi bi-person-fill me-1 text-body-secondary"></i>
                      <span v-if="log.usuarioId === 'Sistema'">Sistema</span>
                      <span v-else-if="isIdNumerico(log.usuarioId)">
                        ({{ log.usuarioId }}) {{ usuariosNomes[log.usuarioId] || '...' }}
                      </span>
                      <span v-else>{{ log.usuarioId }}</span>
                    </td>
                    <td class="text-center">
                      <span class="badge" :class="obterCorAcao(log.tipoAcao)">{{ traduzirAcao(log.tipoAcao) }}</span>
                      <div v-if="log.tabela !== tabela" class="mt-1 fw-bold text-body-secondary" style="font-size: 0.70rem; letter-spacing: 0.5px;">
                        {{ formatarNomeTabela(log.tabela) }}
                      </div>
                    </td>
                    <td class="fw-bold text-body-secondary fst-italic">Todos os Dados</td>
                    <td class="text-danger-emphasis bg-danger-subtle">
                      <span v-if="log.tipoAcao === 'Added'" class="text-body-secondary fw-bold">-</span>
                      <ul v-else class="list-unstyled mb-0 small">
                        <li v-for="(val, key) in filtrarCamposSistema(log.dadosAntigos)" :key="key">
                          <strong class="text-body-emphasis">{{ formatarNomeCampo(key) }}:</strong>
                          <template v-if="isValorJson(log.dadosAntigos, key)">
                            <ul class="mb-0 small ps-3" style="list-style: none;">
                              <li v-for="(v, k) in obterValorJson(log.dadosAntigos, key)" :key="k">
                                <strong class="text-body-emphasis">{{ formatarNomeCampo(k) }}:</strong>
                                <template v-if="typeof v === 'object' && v !== null">{{ JSON.stringify(v) }}</template>
                                <template v-else>{{ v }}</template>
                              </li>
                            </ul>
                          </template>
                          <template v-else>{{ obterValorApresentacao(log.dadosAntigos, key) }}</template>
                        </li>
                      </ul>
                    </td>
                    <td class="text-success-emphasis bg-success-subtle">
                      <span v-if="log.tipoAcao === 'Deleted'" class="text-body-secondary fw-bold">-</span>
                      <ul v-else class="list-unstyled mb-0 small">
                        <li v-for="(val, key) in filtrarCamposSistema(log.dadosNovos)" :key="key">
                          <strong class="text-body-emphasis">{{ formatarNomeCampo(key) }}:</strong>
                          <template v-if="isValorJson(log.dadosNovos, key)">
                            <ul class="mb-0 small ps-3" style="list-style: none;">
                              <li v-for="(v, k) in obterValorJson(log.dadosNovos, key)" :key="k">
                                <strong class="text-body-emphasis">{{ formatarNomeCampo(k) }}:</strong>
                                <template v-if="typeof v === 'object' && v !== null">{{ JSON.stringify(v) }}</template>
                                <template v-else>{{ v }}</template>
                              </li>
                            </ul>
                          </template>
                          <template v-else>{{ obterValorApresentacao(log.dadosNovos, key) }}</template>
                        </li>
                      </ul>
                    </td>
                  </tr>

                </template>
              </tbody>
            </table>
          </div>

          <!-- Controles de Paginação -->
          <div v-if="logsFiltrados.length > 0" class="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3">
            <div class="d-flex align-items-center mb-2 mb-md-0">
              <label class="text-body-secondary small me-2 mb-0">Registos por página:</label>
              <select class="form-select form-select-sm w-auto cursor-pointer" v-model="itensPorPagina" @change="paginaAtual = 1">
                <option v-for="op in [5, 10, 25, 50]" :key="op" :value="op">{{ op }}</option>
              </select>
            </div>
            <div class="d-flex align-items-center">
              <span class="text-body-secondary small me-3">Página {{ paginaAtual }} de {{ totalPaginas || 1 }} (Total: {{ logsFiltrados.length }})</span>
              <button class="btn btn-sm btn-outline-secondary me-1" @click="paginaAtual--" :disabled="paginaAtual <= 1"><i class="bi bi-chevron-left"></i></button>
              <button class="btn btn-sm btn-outline-secondary" @click="paginaAtual++" :disabled="paginaAtual >= totalPaginas"><i class="bi bi-chevron-right"></i></button>
            </div>
          </div>
        </template>
      </div>

      <footer class="modal-footer justify-content-end bg-body-tertiary">
        <button type="button" class="btn btn-secondary" @click="fechar">Fechar</button>
      </footer>
    </div>
  </div>
  </Teleport>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import api from '@/services/api'
import { useMetadataListasStore } from '@/stores/metadataListas'

import { formatarDataHoraBR } from '@/utils/useDataBR'
import { reservarZIndex, liberarZIndex, BASE_ZINDEX } from '@/composables/useZIndex'

const props = defineProps({
  tabela: { type: String, required: true },
  chavePrimaria: { type: [String, Number], required: true },
  tituloRegistro: { type: String, default: '' },
  dicionarios: { type: Object, default: () => ({}) },
  tabelasFilhas: { type: Array, default: () => [] },
  chaveEstrangeira: { type: String, default: '' },
  // GUARDIÃO: Adicionada a prop de contexto para suportar o Master e o Tenant
  contexto: { type: String, default: 'tenant' },
  // Quando true, renderiza apenas o conteúdo (sem backdrop/header/footer) para嵌入 em ModalFormularioV2
  embedded: { type: Boolean, default: false },
  // Controla o empilhamento (quem fica à frente). O modal é teleportado ao body
  // para escapar do stacking context do .content-area do MainDashboard (z-index 1000),
  // garantindo que fique acima do sidebar (z-index 1040) e demais painéis.
  zIndex: { type: Number, default: 11500 },
  // Permite arrastar o modal pela barra de título (mesmo comportamento do ModalFormulario).
  // Apenas a área vazia do header inicia o arrasto, preservando a seleção/cópia dos labels.
  draggable: { type: Boolean, default: true }
})

const emit = defineEmits(['fechar'])

// --- Empilhamento (z-index) via registry central ---
// No modo modal (não embedded) reserva o z-index de forma SÍNCRONA no setup
// (o componente é montado via `v-if` pelo pai; evita um frame na camada base)
// e libera no onUnmounted. A prop `zIndex`, quando informada, atua como piso.
const tokenZIndex = ref(null)
const zIndexReservado = ref(null)
const zIndexEfetivo = computed(() => zIndexReservado.value ?? props.zIndex ?? BASE_ZINDEX)

if (!props.embedded) {
  const reservaInicial = reservarZIndex(props.zIndex)
  tokenZIndex.value = reservaInicial.token
  zIndexReservado.value = reservaInicial.zIndex
}

// --- Estado de arrasto (mesmo padrão do ModalFormulario) ---
const backdropRef = ref(null)
const modalRef = ref(null)
const posX = ref(0)
const posY = ref(0)
const dragging = ref(false)
const dragOffset = ref({ x: 0, y: 0 })
// Evita recentralizar o modal após o utilizador já tê-lo movido manualmente
const jaArrastado = ref(false)

const modalStyle = computed(() => {
  if (!props.draggable || props.embedded) return {}
  return {
    position: 'fixed',
    left: `${posX.value}px`,
    top: `${posY.value}px`,
    transform: 'none',
    margin: '0'
  }
})

const centralizarModal = () => {
  if (!props.draggable || props.embedded || !modalRef.value) return
  const rect = modalRef.value.getBoundingClientRect()
  posX.value = Math.max((window.innerWidth - rect.width) / 2, 0)
  posY.value = Math.max((window.innerHeight - rect.height) / 2, 20)
}

const iniciarArrasto = (e) => {
  if (!props.draggable || props.embedded || !modalRef.value) return
  e.preventDefault()
  dragging.value = true
  const rect = modalRef.value.getBoundingClientRect()
  dragOffset.value = { x: e.clientX - rect.left, y: e.clientY - rect.top }
  document.body.style.userSelect = 'none'
  document.body.style.pointerEvents = 'none'
  document.addEventListener('mousemove', mover)
  document.addEventListener('mouseup', pararArrasto)
}

const mover = (e) => {
  if (!dragging.value) return
  jaArrastado.value = true
  posX.value = e.clientX - dragOffset.value.x
  posY.value = e.clientY - dragOffset.value.y
}

const pararArrasto = () => {
  dragging.value = false
  document.body.style.userSelect = ''
  document.body.style.pointerEvents = ''
  document.removeEventListener('mousemove', mover)
  document.removeEventListener('mouseup', pararArrasto)
}

const limparListenersArrasto = () => {
  document.removeEventListener('mousemove', mover)
  document.removeEventListener('mouseup', pararArrasto)
  document.body.style.userSelect = ''
  document.body.style.pointerEvents = ''
}

const metadataStore = useMetadataListasStore()
const logs = ref([])
const loading = ref(false)
const erro = ref(null)
const usuariosNomes = reactive({})
const dicionariosDinamicos = reactive({})
// Força re-renderização do template após carregar listas assincronamente
const versaoDicionarios = ref(0)

// Termo de busca textual para filtrar logs (independente da paginação)
const termoBusca = ref('')
// Termo de busca efetivo (sincronizado automaticamente para 3+ chars, ou via Enter)
const termoBuscaEfetivo = ref('')

// GUARDIÃO: Estado do switch (ativo por padrão)
const mostrarRelacionados = ref(false)

// GUARDIÃO: Estado e listagem para o filtro por tabela (badges acima do grid)
const tabelaSelecionada = ref('')
const tabelasDisponiveis = computed(() => [...new Set(logs.value.map(l => l.tabela).filter(Boolean))])

const fechar = () => emit('fechar')

// Confirma a busca manualmente (Enter para termos curtos de 1-2 caracteres)
const confirmarBusca = () => {
  termoBuscaEfetivo.value = termoBusca.value
}

// Limpa ambos os termos de busca
const limparBusca = () => {
  termoBusca.value = ''
  termoBuscaEfetivo.value = ''
}

// GUARDIÃO: Alterna o filtro por tabela (clique no badge seleciona/desseleciona)
const alternarFiltroTabela = (tabela) => {
  tabelaSelecionada.value = tabelaSelecionada.value === tabela ? '' : tabela
  paginaAtual.value = 1
  // Garante que logs de tabelas filhas fiquem visíveis ao filtrar por elas
  if (tabelaSelecionada.value && tabelaSelecionada.value !== props.tabela) {
    mostrarRelacionados.value = true
  }
}

// GUARDIÃO: Remove o filtro por tabela ativo
const limparFiltroTabela = () => {
  tabelaSelecionada.value = ''
  paginaAtual.value = 1
}

// --- Paginação ---
const paginaAtual = ref(1)
const itensPorPagina = ref(10)

// GUARDIÃO: Reinicia a página para 1 sempre que o filtro do switch for ativado/desativado
watch(mostrarRelacionados, () => {
  paginaAtual.value = 1
})

// GUARDIÃO: Reinicia a página para 1 sempre que o filtro por tabela mudar
watch(tabelaSelecionada, () => {
  paginaAtual.value = 1
})

// Reinicia a página para 1 quando o termo de busca mudar
// Aplica o filtro imediatamente para qualquer termo (não apenas 3+ caracteres)
watch(termoBusca, (novoValor) => {
  paginaAtual.value = 1
  termoBuscaEfetivo.value = novoValor
})

// GUARDIÃO: Lógica de ordenação com filtro relacional e expurgo de logs sem alterações reais
const logsOrdenados = computed(() => {
  let baseLogs = logs.value

  if (!mostrarRelacionados.value) {
    baseLogs = baseLogs.filter(l => l.tabela === props.tabela)
  }

  // GUARDIÃO: Filtro ativo via badges de tabela
  if (tabelaSelecionada.value) {
    baseLogs = baseLogs.filter(l => l.tabela === tabelaSelecionada.value)
  }

  // Expurga logs de "Modified" que não resultaram em alterações reais visíveis
  baseLogs = baseLogs.filter(log => {
    if (log.tipoAcao === 'Modified') {
      return obterColunasFiltradas(log).length > 0
    }
    return true // Mantém Added e Deleted intactos
  })

  return [...baseLogs].sort((a, b) => new Date(b.dataHora) - new Date(a.dataHora))
})

// Aplica filtro de busca textual sobre os logs ordenados (antes da paginação)
const logsFiltrados = computed(() => {
  const termo = termoBuscaEfetivo.value.toLowerCase().trim()
  if (!termo) return logsOrdenados.value

  return logsOrdenados.value.filter(log => {
    // Busca na chave primária (ID do registro)
    if (log.chavePrimaria?.toString().toLowerCase().includes(termo)) return true
    // Busca no nome da tabela
    if (log.tabela?.toLowerCase().includes(termo)) return true
    // Busca no tipo de ação
    if (log.tipoAcao?.toLowerCase().includes(termo)) return true
    // Busca no ID do usuário
    if (log.usuarioId?.toString().toLowerCase().includes(termo)) return true
    // Busca no nome do usuário (resolvido)
    const nomeUsuario = usuariosNomes[log.usuarioId]
    if (nomeUsuario?.toLowerCase().includes(termo)) return true
    // Busca nos dados antigos/novos (conteúdo dos valores)
    if (log.dadosAntigos?.toLowerCase().includes(termo)) return true
    if (log.dadosNovos?.toLowerCase().includes(termo)) return true
    // Busca nas colunas afetadas
    if (log.colunasAfetadas?.toLowerCase().includes(termo)) return true
    return false
  })
})

const totalPaginas = computed(() => Math.ceil(logsFiltrados.value.length / itensPorPagina.value))

const logsPaginados = computed(() => {
  const inicio = (paginaAtual.value - 1) * itensPorPagina.value
  const fim = inicio + itensPorPagina.value
  return logsFiltrados.value.slice(inicio, fim)
})

// --- Utilitário de Identificação ---
const isIdNumerico = (id) => {
  return id && /^\d+$/.test(String(id))
}

// --- Busca Inteligente de Nomes de Usuários ---
const carregarNomesUsuarios = async (logsData) => {
  const idsUnicos = [...new Set(logsData.map(l => l.usuarioId).filter(id => id && id !== 'Sistema' && isIdNumerico(id)))]

  const promessas = idsUnicos.map(async (id) => {
    if (!usuariosNomes[id]) {
      try {
        const response = await api.get(`/Usuarios/${id}`)
        usuariosNomes[id] = response.data.nome
      } catch (err) {
        usuariosNomes[id] = 'Desconhecido'
      }
    }
  })

  await Promise.all(promessas)
}

/**
 * GUARDIÃO: Busca automática de dicionários baseada em metadados do banco Master
 */
const carregarDicionariosFaltantes = async (logsData) => {
  await metadataStore.carregarMetadados()

  const camposLstNecessarios = []
  logsData.forEach(log => {
    // 1. Verifica colunas afetadas (Modified)
    const colunas = parseJsonFallback(log.colunasAfetadas)
    if (Array.isArray(colunas)) {
      colunas.forEach(col => {
        if (col.toLowerCase().startsWith('lst') && !props.dicionarios[col]) {
          camposLstNecessarios.push({ tabela: log.tabela, campo: col })
        }
      })
    }
    // 2. Verifica dados brutos (Added/Deleted)
    if (log.tipoAcao !== 'Modified') {
      const dados = log.tipoAcao === 'Added' ? log.dadosNovos : log.dadosAntigos
      const obj = parseJsonFallback(dados)
      Object.keys(obj).forEach(key => {
        if (key.toLowerCase().startsWith('lst') && !props.dicionarios[key]) {
          camposLstNecessarios.push({ tabela: log.tabela, campo: key })
        }
      })
    }
  })

  // Remove duplicados
  const unicos = camposLstNecessarios.filter((v, i, a) =>
    a.findIndex(t => t.tabela === v.tabela && t.campo === v.campo) === i
  )

  // GUARDIÃO: Também pré-carrega listas para todas as tabelas filhas declaradas na prop,
  // mesmo que nenhum log delas tenha sido retornado nesta consulta.
  if (props.tabelasFilhas && props.tabelasFilhas.length > 0) {
    const mappingsFilhas = metadataStore.mapeamentos.filter(m =>
      props.tabelasFilhas.some(t => t.toLowerCase() === m.tabelaNome?.toLowerCase()) &&
      m.campoNome?.toLowerCase().startsWith('lst') &&
      !unicos.some(u => u.tabela === m.tabelaNome && u.campo === m.campoNome)
    )
    mappingsFilhas.forEach(m => {
      if (!unicos.some(u => u.tabela === m.tabelaNome && u.campo === m.campoNome)) {
        unicos.push({ tabela: m.tabelaNome, campo: m.campoNome })
      }
    })
  }

  const promessas = unicos.map(async ({ tabela, campo }) => {
    const mapping = metadataStore.getMapping(tabela, campo)
    if (mapping) {
      const lista = await metadataStore.getListaLookup(mapping.codigoLista, mapping.tipoLista)
      // Usa chave composta tabela__campo para evitar colisão entre tabelas diferentes
      dicionariosDinamicos[`${tabela}__${campo}`] = lista
    }
  })

  await Promise.all(promessas)
  // Força re-renderização do template após carregar todas as listas
  versaoDicionarios.value++
}

// --- Operações API ---
const buscarLogs = async () => {
  loading.value = true
  erro.value = null
  try {
    const params = new URLSearchParams()
    params.append('tabela', props.tabela)
    params.append('chavePrimaria', props.chavePrimaria)

    // GUARDIÃO: Enviando o contexto para a API saber em qual banco procurar
    params.append('contexto', props.contexto)

    if (props.chaveEstrangeira) {
      params.append('chaveEstrangeira', props.chaveEstrangeira)
    }

    if (props.tabelasFilhas && props.tabelasFilhas.length > 0) {
      props.tabelasFilhas.forEach(t => params.append('tabelasFilhas', t))
    }

    const response = await api.get(`/AuditLogs?${params.toString()}`)
    logs.value = response.data

    await Promise.all([
      carregarNomesUsuarios(response.data),
      carregarDicionariosFaltantes(response.data)
    ])
  } catch (err) {
    console.error('Erro ao buscar logs de auditoria:', err)
    erro.value = 'Não foi possível carregar o histórico de alterações.'
  } finally {
    loading.value = false
  }
}

// --- Utilitários Visuais ---
const traduzirAcao = (acao) => {
  const mapa = { Added: 'Criação', Modified: 'Edição', Deleted: 'Exclusão' }
  return mapa[acao] || acao
}

const obterCorAcao = (acao) => {
  const mapa = { Added: 'bg-success', Modified: 'bg-primary', Deleted: 'bg-danger' }
  return mapa[acao] || 'bg-secondary'
}

const parseJsonFallback = (jsonString) => {
  if (!jsonString) return {}
  try {
    return typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString
  } catch (e) {
    return {}
  }
}

const filtrarCamposSistema = (jsonObj) => {
  const obj = parseJsonFallback(jsonObj)
  if (!obj || typeof obj !== 'object') return {}

  const objFiltrado = {}
  for (const [key, value] of Object.entries(obj)) {
    const lowerKey = key.toLowerCase()
    if (!lowerKey.includes('criacao') && !lowerKey.includes('modificacao') && !lowerKey.includes('xmin')) {
      objFiltrado[key] = value
    }
  }
  return objFiltrado
}

// GUARDIÃO: Substitui o 'obterColunasAfetadas' cego por um filtro inteligente que cruza valores
const obterColunasFiltradas = (log) => {
  const parsed = parseJsonFallback(log.colunasAfetadas)
  if (!Array.isArray(parsed)) return []

  return parsed.filter(coluna => {
    const lowerCol = coluna.toLowerCase()
    if (lowerCol.includes('criacao') || lowerCol.includes('modificacao') || lowerCol.includes('xmin')) return false

    const valAntigo = obterValorApresentacao(log.dadosAntigos, coluna)
    const valNovo = obterValorApresentacao(log.dadosNovos, coluna)

    // Bloqueia a exibição de colunas onde ambos os valores são "Nulo/Vazio"
    if (valAntigo === 'Nulo/Vazio' && valNovo === 'Nulo/Vazio') return false

    // Bloqueia a exibição se os valores acabaram sendo estritamente iguais (Ruído do EF Core)
    if (valAntigo === valNovo) return false

    return true
  })
}

const obterValorApresentacao = (jsonObj, campo) => {
  const obj = parseJsonFallback(jsonObj)
  if (!obj) return 'Nulo/Vazio'

  const chaveReal = Object.keys(obj).find(k => k.toLowerCase() === campo.toLowerCase())
  const valor = chaveReal ? obj[chaveReal] : null

  if (valor === null || valor === undefined) return 'Nulo/Vazio'

  // 1. Tenta tradução via props.dicionarios (manual)
  const chaveDicionarioManual = Object.keys(props.dicionarios).find(k => k.toLowerCase() === campo.toLowerCase())
  let listaTraducoes = chaveDicionarioManual ? props.dicionarios[chaveDicionarioManual] : null

  // 2. Se não encontrou, tenta tradução via dicionariosDinamicos (automático via metadados)
  // A chave composta é "Tabela__Campo" — primeiro tenta com a tabela do log atual,
  // depois faz fallback apenas pelo nome do campo para compatibilidade.
  if (!listaTraducoes) {
    // Tenta buscar pela chave composta primeiro (tabela + campo)
    const chaveComposta = Object.keys(dicionariosDinamicos).find(k => {
      const partes = k.split('__')
      return partes.length === 2 && partes[1]?.toLowerCase() === campo.toLowerCase()
    })
    listaTraducoes = chaveComposta ? dicionariosDinamicos[chaveComposta] : null
  }
  if (Array.isArray(listaTraducoes)) {
    const matchVal = String(valor)
    const itemEncontrado = listaTraducoes.find(item => {
      const match = (item.idListaSistemaItem != null && String(item.idListaSistemaItem) === matchVal) ||
                    (item.idListaClienteItem != null && String(item.idListaClienteItem) === matchVal) ||
                    (item.id != null && String(item.id) === matchVal) ||
                    (item.textoLista != null && String(item.textoLista) === matchVal)
      return match
    })

    if (itemEncontrado) {
      return itemEncontrado.textoLista || itemEncontrado.descricao || itemEncontrado.nome || String(valor)
    }
  }

  if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não'
  if (typeof valor === 'object') return JSON.stringify(valor)

  const stringValor = String(valor)
  const isIsoDate = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(stringValor)
  const isOnlyDate = /^\d{4}-\d{2}-\d{2}$/.test(stringValor)

  if (isIsoDate || isOnlyDate) {
    const parsedDate = new Date(stringValor)
    if (!isNaN(parsedDate)) {
      if (isOnlyDate) {
        return parsedDate.toLocaleDateString('pt-BR', { timeZone: 'UTC' })
      }
      return parsedDate.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
    }
  }

  return stringValor
}

/**
 * Verifica se o valor de um campo num log de auditoria é uma string JSON
 * que representa um objeto plano (não array) para exibição formatada.
 */
const isValorJson = (jsonObj, campo) => {
  const obj = parseJsonFallback(jsonObj)
  if (!obj) return false
  const chaveReal = Object.keys(obj).find(k => k.toLowerCase() === campo.toLowerCase())
  const valor = chaveReal ? obj[chaveReal] : null
  if (!valor || typeof valor !== 'string') return false
  const trimmed = valor.trim()
  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return false
  try {
    const parsed = JSON.parse(trimmed)
    // Só considera como JSON formatável se for um objeto plano (não array, não null)
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
  } catch {
    return false
  }
}

/**
 * Obtém o valor de um campo parseando-o como JSON.
 * Retorna o objeto parseado (com chaves internas filtradas), ou null se não for JSON válido ou for array.
 * Chaves que contenham "info" ou "config" (case-insensitive) são omitidas — são metadados internos.
 */
const obterValorJson = (jsonObj, campo) => {
  const obj = parseJsonFallback(jsonObj)
  if (!obj) return null
  const chaveReal = Object.keys(obj).find(k => k.toLowerCase() === campo.toLowerCase())
  const valor = chaveReal ? obj[chaveReal] : null
  if (!valor || typeof valor !== 'string') return null
  try {
    const parsed = JSON.parse(valor.trim())
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null
    // Filtra chaves internas que contenham "info" ou "config" (case-insensitive)
    // Ex: "TemplateInfo", "TemplateConfig" são metadados de formatação, não dados de negócio
    const filtrado = {}
    for (const [k, v] of Object.entries(parsed)) {
      const lowerK = k.toLowerCase()
      if (lowerK.includes('info') || lowerK.includes('config')) continue
      filtrado[k] = v
    }
    return Object.keys(filtrado).length > 0 ? filtrado : null
  } catch {
    return null
  }
}

const formatarNomeCampo = (nome) => {
  if (!nome) return nome

  let nomeLimpo = nome

  // Detecta prefixo Ts (TimeStamp) — ex: TsCriacao → "Momento da Criação"
  let prefixoTs = false
  if (/^[tT]s[A-Z]/.test(nomeLimpo)) {
    prefixoTs = true
    nomeLimpo = nomeLimpo.substring(2)
  }

  // Remove prefixo Lst (case-insensitive)
  if (/^[lL]st/i.test(nomeLimpo)) {
    nomeLimpo = nomeLimpo.substring(3)
  }

  // Remove prefixo Is (case-insensitive) — ex: IsAtivo → Ativo
  if (/^[iI]s[A-Z]/.test(nomeLimpo)) {
    nomeLimpo = nomeLimpo.substring(2)
  }

  // Se for snake_case, substitui underscores por espaços e capitaliza
  if (nomeLimpo.includes('_')) {
    nomeLimpo = nomeLimpo
      .split('_')
      .filter(Boolean)
      .map(palavra => palavra.charAt(0).toUpperCase() + palavra.slice(1).toLowerCase())
      .join(' ')
    return prefixoTs ? `Momento da ${nomeLimpo}` : nomeLimpo
  }

  // Se for PascalCase/camelCase, insere espaços antes de maiúsculas
  nomeLimpo = nomeLimpo.replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')

  // Remove sufixo "Json" (case-insensitive) do nome do campo — ex: "Atributos Precificacao Json" → "Atributos Precificacao"
  nomeLimpo = nomeLimpo.replace(/\s+[Jj][Ss][Oo][Nn]$/, '')

  return prefixoTs ? `Momento da ${nomeLimpo}` : nomeLimpo
}

// GUARDIÃO: Formata o nome da tabela com o mesmo tratamento aplicado aos nomes de campos
const formatarNomeTabela = (nome) => {
  if (!nome) return nome

  let nomeLimpo = nome

  // Se for snake_case, substitui underscores por espaços e capitaliza
  if (nomeLimpo.includes('_')) {
    return nomeLimpo
      .split('_')
      .filter(Boolean)
      .map(palavra => palavra.charAt(0).toUpperCase() + palavra.slice(1).toLowerCase())
      .join(' ')
  }

  // Se for PascalCase/camelCase, insere espaços antes de maiúsculas
  nomeLimpo = nomeLimpo.replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')

  return nomeLimpo
}

// Recentraliza o modal após o carregamento inicial (o conteúdo assíncrono altera a altura),
// desde que o utilizador ainda não o tenha arrastado.
watch(loading, (estaCarregando) => {
  if (!estaCarregando && !props.embedded && !jaArrastado.value) {
    nextTick(() => centralizarModal())
  }
})

onMounted(() => {
  if (!props.embedded) {
    nextTick(() => centralizarModal())
  }
  if (props.tabela && props.chavePrimaria) {
    buscarLogs()
  }
})

onUnmounted(() => {
  limparListenersArrasto()
  liberarZIndex(tokenZIndex.value)
  tokenZIndex.value = null
  zIndexReservado.value = null
})
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-container {
  width: 95%;
  max-width: 1140px;
  background-color: var(--bs-body-bg);
  color: var(--bs-body-color);
  border: 1px solid var(--bs-border-color-translucent);
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.modal-header {
  padding: 1rem 1.5rem;
  background-color: #34495E;
  color: #FFFFFF;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-bottom: 1px solid #1f2d3a;
  flex-shrink: 0;
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 500;
  margin: 0;
}

.modal-body {
  overflow-y: auto;
  flex-grow: 1;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--bs-border-color-translucent);
  flex-shrink: 0;
  display: flex;
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.text-sm {
  font-size: 0.85rem;
}

.table th, .table td {
  padding: 0.6rem 0.75rem;
}

.cursor-pointer {
  cursor: pointer;
}

/* Arrasto do modal (mesmo padrão do ModalFormulario) */
.modal-backdrop-draggable {
  display: block;
}

.cursor-grab { cursor: grab; }
.cursor-grabbing { cursor: grabbing; user-select: none; }

/* GUARDIÃO: mantém a área de título/informações do header selecionável e copiável,
   já que o arrasto só é iniciado no espaço vazio do header (@mousedown.self). */
.modal-header-info {
  cursor: text;
  user-select: text;
}

/* Formatamento do Switch */
.switch-md {
  width: 2em;
  height: 1em;
  cursor: pointer;
}

.list-unstyled li {
  margin-bottom: 0.2rem;
  border-bottom: 1px dashed var(--bs-border-color-translucent);
  padding-bottom: 0.2rem;
}
.list-unstyled li:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}
</style>
